import { useLanguage } from '@context/LanguageContext'
import { useDocumentMetadata } from '@hooks/useDocumentMetadata'
import StaticLayout from '../layout/StaticLayout'
import { useNamespaces } from '@hooks/useNamespaces'
import { ROUTE_NAMESPACES, LANGUAGES_WITH_LEGAL } from '@i18n/namespaces'
import ScreenFallback from '@components/layout/ScreenFallback'
import styles from './Terms.module.css'
import noticeStyles from './legalNotice.module.css'

export default function Terms () {
  const { t, lang } = useLanguage()
  const { isLoading: namespacesLoading } = useNamespaces(ROUTE_NAMESPACES.terms)
  useDocumentMetadata()

  if (namespacesLoading) {
    return <ScreenFallback />
  }
  const showLegalNotice = !LANGUAGES_WITH_LEGAL.includes(lang)

  return (
    <StaticLayout>
      <div className={styles.termsPage}>
        {showLegalNotice && (
          <div className={noticeStyles.notice} role='alert'>
            <p>{t.legal.notice}</p>
          </div>
        )}
        <header className={styles.header}>
          <h1 className={styles.title}>{t.terms.title}</h1>
          <p className={styles.date}>{t.terms.lastUpdated}</p>
        </header>

        <div className={styles.content}>
          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>{t.terms.agreement.title}</h2>
            <p className={styles.paragraph}>
              {t.terms.agreement.intro.company} {t.terms.agreement.intro.services}
            </p>
            <p className={styles.paragraph}>
              {t.terms.agreement.description}
            </p>
            <p className={styles.paragraph}>
              {t.terms.agreement.contact}
            </p>
            <p className={styles.paragraph}>
              {t.terms.agreement.binding}
            </p>
            <p className={styles.paragraph}>
              {t.terms.agreement.changes}
            </p>
            <p className={styles.paragraph}>
              {t.terms.agreement.ageRestriction}
            </p>
            <p className={styles.paragraph}>
              {t.terms.agreement.printCopy}
            </p>
          </section>

          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>{t.terms.tableOfContents.title}</h2>
            <nav className={styles.toc}>
              {t.terms.tableOfContents.items.map((item, index) => (
                <a key={index} href={item.href} className={styles.tocLink}>
                  {item.text}
                </a>
              ))}
            </nav>
          </section>

          {t.terms.sections.map((section, index) => (
            <section key={index} className={styles.section} id={section.id}>
              <h2 className={styles.sectionTitle}>{section.title}</h2>

              {section.subsections?.map((subsection, subIndex) => (
                <div key={subIndex} className={styles.subsection}>
                  <h3 className={styles.subsectionTitle}>{subsection.title}</h3>
                  {subsection.content.map((content, contentIndex) => (
                    <div key={contentIndex} className={styles.contentBlock}>
                      {content.type === 'paragraph' && (
                        <p className={styles.paragraph}>{content.text}</p>
                      )}

                      {content.type === 'list' && (
                        <ul className={styles.list}>
                          {content.items.map((item, itemIndex) => (
                            <li key={itemIndex} className={styles.listItem}>
                              {item}
                            </li>
                          ))}
                        </ul>
                      )}

                      {content.type === 'table' && (
                        <div className={styles.tableWrapper}>
                          <table className={styles.table}>
                            <thead>
                              <tr>
                                {content.headers.map((header, headerIndex) => (
                                  <th key={headerIndex} className={styles.tableHeader}>
                                    {header}
                                  </th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {content.rows.map((row, rowIndex) => (
                                <tr key={rowIndex} className={styles.tableRow}>
                                  {row.map((cell, cellIndex) => (
                                    <td key={cellIndex} className={styles.tableCell}>
                                      {cell}
                                    </td>
                                  ))}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}

                      {content.type === 'emphasis' && (
                        <p className={styles.emphasis}>{content.text}</p>
                      )}
                    </div>
                  ))}
                </div>
              ))}

              {!section.subsections && section.content.map((content, contentIndex) => (
                <div key={contentIndex} className={styles.contentBlock}>
                  {content.type === 'paragraph' && (
                    <p className={styles.paragraph}>{content.text}</p>
                  )}

                  {content.type === 'list' && (
                    <ul className={styles.list}>
                      {content.items.map((item, itemIndex) => (
                        <li key={itemIndex} className={styles.listItem}>
                          {item}
                        </li>
                      ))}
                    </ul>
                  )}

                  {content.type === 'table' && (
                    <div className={styles.tableWrapper}>
                      <table className={styles.table}>
                        <thead>
                          <tr>
                            {content.headers.map((header, headerIndex) => (
                              <th key={headerIndex} className={styles.tableHeader}>
                                {header}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {content.rows.map((row, rowIndex) => (
                            <tr key={rowIndex} className={styles.tableRow}>
                              {row.map((cell, cellIndex) => (
                                <td key={cellIndex} className={styles.tableCell}>
                                  {cell}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {content.type === 'emphasis' && (
                    <p className={styles.emphasis}>{content.text}</p>
                  )}
                </div>
              ))}
            </section>
          ))}
        </div>
      </div>
    </StaticLayout>
  )
}
